import MobileAppImageCut from "@/public/assets/img/mobile-app-preview-cut.png"
import MobileAppImage from "@/public/assets/img/mobile-app-preview.png"
import Link from "@/src/common/components/Link"
import WelcomeBanner from "@/src/common/components/WelcomeBanner"
import useStearsRouter from "@/src/common/hooks/useStearsRouter"
import useTryLocation from "@/src/common/hooks/useTryLocation"
import { OperatingSystems } from "@/src/common/types"
import { MY_ELECTIONS_COPY } from "@/src/elections/business-layer/constants"
import { URLFactory } from "@/src/elections/business-layer/factories/URLFactory"
import useElectionArticles from "@/src/elections/business-layer/hooks/useElectionArticles"
import { ElectionArticleInfo } from "@/src/elections/business-layer/types"
import Breadcrumbs from "@/src/elections/components/Breadcrumbs"
import Layout from "@/src/elections/components/Layout"
import Template from "@/src/elections/components/Template"
import { ElectionsAPIInstance } from "@/src/elections/data-layer"
import externalHTTP from "@/src/elections/data-layer/datasources/ExternalHTTP"
import { Constituency } from "@/src/elections/data-layer/types"
import { useAnalytics } from "@/src/sb/components/Analytics"
import { SEO } from "@/src/sb/components/SEO"
import {
  MOBILE_ANDROID_DOWNLOAD_LINK,
  MOBILE_APP_DOWNLOAD_LINK,
  MOBILE_APP_SCHEME_MY_ELECTIONS_PREFERENCE,
  MOBILE_IOS_DOWNLOAD_LINK
} from "@/src/sb/constants/configs"
import { useWindowSize } from "@/src/sb/hooks/useWindowSize"
import { GetStaticPropsResult } from "next"
import { GetStaticPropsContext, NextPage } from "next"
import Head from "next/head"
import Image from "next/legacy/image"
import React, { useEffect } from "react"

const MyElections: NextPage<MyElectionsProps> = () => {
  const { label: title, description } = MY_ELECTIONS_COPY
  const analytics = useAnalytics()
  const router = useStearsRouter()
  const [mobile] = useWindowSize()
  const onClickSelectElections = useTryLocation({
    location: MOBILE_APP_SCHEME_MY_ELECTIONS_PREFERENCE,
    fallback: MOBILE_APP_DOWNLOAD_LINK
  })

  useEffect(() => {
    analytics.viewMyElectionsPage()
  }, [analytics])

  const onClickStore = (os: OperatingSystems) => {
    analytics?.clickDownloadMobileApp(os)
  }

  const { articles } = useElectionArticles(externalHTTP)

  const onClickMyElections = (
    source: "my-elections-cta" | "nav-follow-races"
  ) => {
    analytics.clickMyElections({ source })
  }

  const onClickArticle = ({ title, slug }: ElectionArticleInfo) => {
    analytics.clickArticleInFeed({
      page: router.pathname,
      title,
      slug
    })
  }

  const MyElectionHeader = (
    <WelcomeBanner
      title="Get live results on your phone!"
      className="py-4 lg:py-[46px]"
    />
  )

  return (
    <>
      <SEO
        title="Personalised election updates - Stears Elections"
        description="Get live personalised election updates, insights and results for races and candidates you care about. Get started now."
        imageURL={URLFactory.absolute("socialCardImage")}
        linkedData={{
          "@context": "https://schema.org",
          "@type": "ChooseAction",
          name: "Personalised election updates"
        }}
      />
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Layout onClickFollowRaces={() => onClickMyElections("nav-follow-races")}>
        <div className="hidden md:block">{MyElectionHeader}</div>

        <Template.Container>
          <Template.Page.TwoColumns
            showSignupCTA={false}
            onClickMyElections={() => onClickMyElections("my-elections-cta")}
            onClickArticle={onClickArticle}
            articles={!mobile ? articles : []}
          >
            <Template.Card.PreElection>
              <section className="sticky top-[80px] md:static z-[1020]">
                <Breadcrumbs.Nav
                  className="lg:py-0"
                  items={[
                    {
                      href: URLFactory.root(router.query.year as string),
                      text: "Home"
                    },
                    { text: title }
                  ]}
                />

                <div className="md:hidden">{MyElectionHeader}</div>
              </section>

              <section className="px-8 mt-[18px] md:flex text-center md:text-left">
                <div className="md:flex-1 md:order-1">
                  <div className="hidden md:block">
                    <Image
                      src={MobileAppImage}
                      alt="Mobile App"
                      width={380}
                      height={565}
                    />
                  </div>

                  <div className="md:hidden">
                    <Image
                      src={MobileAppImageCut}
                      alt="Mobile App"
                      width={358}
                      height={320}
                    />
                  </div>
                </div>

                <section className="mt-6 md:mt-11 md:mr-4 md:flex-1 md:order-0">
                  <h2 className="text-[18px] leading-5 mb-6 md:text-[2.5rem] font-bold font-jakarta md:leading-10 md:mb-12">
                    Download our app to receive real time results on your phone
                  </h2>
                  <footer>
                    <Link legacyBehavior href={MOBILE_ANDROID_DOWNLOAD_LINK}>
                      <a
                        className="inline-block mr-4"
                        onClick={() => onClickStore("android")}
                      >
                        <Image
                          src="/assets/icons/playstore-download.png"
                          width={mobile ? 142 : 155}
                          height={mobile ? 42 : 44}
                          alt="Android"
                        />
                      </a>
                    </Link>

                    <Link legacyBehavior href={MOBILE_IOS_DOWNLOAD_LINK}>
                      <a
                        className="inline-block"
                        onClick={() => onClickStore("iOS")}
                      >
                        <Image
                          src="/assets/icons/ios-download-v2.png"
                          width={mobile ? 125 : 131}
                          height={mobile ? 42 : 44}
                          alt="iOS"
                        />
                      </a>
                    </Link>

                    <div className="mt-8 hidden md:block">
                      <div className="inline-block border border-stears_black rounded max-w-[128px] text-center pt-1 pb-2">
                        <Image
                          src="/assets/img/qr-code--my-elections.png"
                          width={118}
                          height={118}
                          alt="QR Code"
                        />
                        <p className="text-sm px-2 leading-[14px]">
                          Scan QR code to download
                        </p>
                      </div>
                    </div>

                    <section className="mt-[30px] pb-[45px] md:hidden">
                      <p className="text-sm leading-7 mb-4">
                        Already have the Stears app?
                      </p>

                      <button
                        className="text-xs text-white bg-stears_black py-2.5 px-4 rounded-full"
                        onClick={onClickSelectElections}
                      >
                        Select your elections
                      </button>
                    </section>
                  </footer>
                </section>
              </section>
            </Template.Card.PreElection>
          </Template.Page.TwoColumns>
        </Template.Container>
      </Layout>
    </>
  )
}

type MyElectionsProps = {
  constituencies: Constituency[]
}

export async function getStaticProps(
  _: GetStaticPropsContext
): Promise<GetStaticPropsResult<MyElectionsProps>> {
  const constituencies = await ElectionsAPIInstance.fetchAllConstituencies()
  return {
    revalidate: false,
    props: { constituencies: constituencies?.items || [] }
  }
}

export default MyElections
